import _ from 'lodash';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import {getReferralsColor} from '../../../../controls/agyle/tasks/taskUtil';
import PatientSafetyChecklistIconStatus
    from '../../forms/patientSafetyChecklist/components/patientSafetyChecklistIconStatus';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {referredToService} from '../../../../store/reducers/dicts';
import {getConfig} from '../../../../util/configUtil';
import {useTranslation} from 'react-i18next';
import {hasOneOfPermission, Permissions} from '../../../../store/reducers/user';
import useOpenState from '../../../../hooks/useOpenState';
import {SepsisPathwayStatus} from '../../sepsis/sepsisUtil';
import {AgyleNamedIcon, isIconNameValid} from '../../../../components/icons/agyle';
import {changePatientContextTab, ctxPatientType} from '../../../../store/reducers/patientContext';
import moment from 'moment';
import {obsStatuses} from '../../forms/observation/obsUtils';
import RestoreIcon from '@mui/icons-material/Restore';
import SnoozeIcon from '@mui/icons-material/Snooze';
import BlockIcon from '@mui/icons-material/Block';
import DischargeLetterModal from '../../../../components/modal/dischargeLetterModal';
import makeStyles from '@mui/styles/makeStyles';
import {additionalData} from '../../../../store/reducers/additionalData';
import {getPatientFlagValue} from '../util/patientFlagsUtil';
import {PATIENT_CONTEXT_TYPE} from '../../../../util/patientContextUtil';
import {PATIENT_CONTEXT_TABS_ID} from '../../forms/formsUtil';

const useStyles = makeStyles((theme) => {
    return {
        statusIconSpec: {
            color: '#fff',
            backgroundColor: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4,
            width: 40,
            height: 35,
            marginTop: 0,
            cursor: 'grabbing',
            '-moz-user-select': 'none',
            '-webkit-user-select': 'none',
            userSelect: 'none',
            fontWeight: 'bold',
            fontSize: 12,
            '&.completed': {
                border: '1px solid #000',
                color: '#222222',
            },
            '&.dementia': {
                width: 90,
            },
            '& svg': {
                transform: `scale(1)`,
            },
        },
        singleIconFlag: {
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            width: 36,
            height: 36,
            '&.padding': {
                padding: `${(36 - 24) / 2}px 0`,
            },
            '&.active': {
                backgroundColor: theme.palette.flags.singleIconFlag.active,
                color: theme.palette.flags.singleIconFlag.activeColor,
            },
            '&.ice': {
                height: 35,
                width: 35,
                borderRadius: 4,
                backgroundColor: theme.palette.flags.singleIconFlag.ice,
                color: theme.palette.flags.singleIconFlag.iceColor,
                fontSize: 20,
                alignItems: 'center',
                cursor: 'pointer',
            },
            '&.none': {
                color: theme.palette.flags.singleIconFlag.noneColor,
            },
            '&.confirm': {
                color: theme.palette.flags.singleIconFlag.confirmColor,
            },
            '&.suspected': {
                color: theme.palette.flags.singleIconFlag.suspectedColor,
            },
            '&.sepsis': {
                color: '#dc4a5d',
            },
        },
    };
});

const MainScreenIcons = ({patient}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    // Permissions
    const canViewSepsis = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_MANAGE_SEPSIS, Permissions.AGYLE_CAN_VIEW_SEPSIS]));

    const [isDLSOpen, openDLS, closeDLS] = useOpenState(false, true);

    // Selectors
    const referralsDict = useSelector(referredToService);
    const patientAddData = useSelector(additionalData);
    const patientType = useSelector(ctxPatientType);

    // Configs
    const pscIconStatus = getConfig('patientSafetyChecklist.iconStatus', false);
    const sepsisShowStatusIcon = getConfig('sepsisShowStatusIcon', false);

    // Icons methods
    const getSepsisIcon = () => {
        if (!sepsisShowStatusIcon) return null;

        const incompleteSepsisPath = patientAddData.other?.sepsisPathways
            ? _.find(patientAddData.other.sepsisPathways, ['statusObj', SepsisPathwayStatus.INCOMPLETE])
            : null;

        return (
            <>
                {canViewSepsis && incompleteSepsisPath
                    && !patient.flags?.some((f) => f.name === 'neutropenicSepsis' || f.name === 'sepsis') && (
                        <div
                            key="sepsis"
                            className={clsx(classes.singleIconFlag, 'padding', 'sepsis')}
                        >
                            <AgyleNamedIcon
                                name={incompleteSepsisPath.neutropenic ? 'neutropenicSepsis' : 'sepsis'}
                                withTooltip
                            />
                        </div>
                    )}
            </>
        );
    };

    const getDementiaIcon = () => {
        if (getPatientFlagValue(patient, 'dementia')) {
            return (
                <span className={clsx(classes.statusIconSpec, 'dementia')}>
                    {t('DEMENTIA')}
                </span>
            );
        }
    };

    const getInvestigationsIcon = () => {
        const count = patient?.metadata?.iceReportsCount;
        const iseReset = patient?.metadata?.iceReset?.counter;
        const iceCount = count ? (iseReset ? count - iseReset : count) : 0;

        return (
            (iceCount > 0 || !!iseReset) &&
            <div
                key="ice"
                className={clsx(classes.singleIconFlag, 'ice')}
                onClick={() => dispatch(changePatientContextTab(PATIENT_CONTEXT_TABS_ID.ICE))}
            >
                {iceCount > 99 ? 99 : iceCount < 0 ? 0 : iceCount}
            </div>
        );
    };

    const getInfectionsIcon = () => {
        const infections = _.filter(patientAddData?.other?.alarms, a => {
            return a.group === 'INFECTION CONTROL' && !a.deleted && moment(a.expireDate).isSameOrAfter(moment());
        });

        return infections.map(infection => {
            const status = infection?.state;
            let iconName = '';
            if (infection?.name === 'COVID 19') {
                switch (status) {
                    case 'confirm':
                        iconName = 'covidPositive';
                        break;
                    case 'none':
                        iconName = 'covidNegative';
                        break;
                    case 'suspected':
                    default:
                        iconName = 'covid';
                        break;
                }
            } else {
                const suffix = status === 'suspected' ? 'S' : '';
                iconName = _.toLower(infection.name) + suffix;
            }
            const iconValid = isIconNameValid(iconName);

            return (
                iconValid &&
                <div
                    key={infection.name}
                    className={clsx(classes.singleIconFlag, status, 'padding')}
                >
                    <AgyleNamedIcon name={iconName} withTooltip/>
                </div>
            );
        });
    };

    const getNextObsStatusIcon = () => {
        const nextObsStatus = patient?.patient?.lastObservation?.nextObsStatus;

        if (nextObsStatus) {
            let icon;
            let text;
            // eslint-disable-next-line default-case
            switch (nextObsStatus) {
                case obsStatuses.deferred:
                    icon = <RestoreIcon/>;
                    text = t('Next observation is deferred');
                    break;
                case obsStatuses.suspended:
                    icon = <SnoozeIcon/>;
                    text = t('Next observation is suspended');
                    break;
                case obsStatuses.cancelled:
                    icon = <BlockIcon/>;
                    text = t('Next observation is cancelled');
                    break;
            }

            if (!icon) return;

            return (
                <span>
                    <Tooltip title={text}>
                        <div
                            key="nextObsStatus"
                            className={clsx(classes.singleIconFlag, 'padding')}
                        >
                            {icon}
                        </div>
                    </Tooltip>
                </span>
            );
        }
    };

    const getDLStatusIcon = () => {
        if (_.isUndefined(patient?.studies?.dischargeLetter?.sendStatus)) return null;

        let status = patient?.studies?.dischargeLetter?.sendStatus === 'OK' || false;
        return (
            <div
                key="dl"
                className={clsx(classes.singleIconFlag, 'padding')}
                onClick={() => status ? null : openDLS()}
            >
                <AgyleNamedIcon
                    name={status ? 'dlsSuccess' : 'dlsError'}
                    withTooltip
                />
            </div>
        );
    };

    const getReferralIcon = () => {
        if (_.isNil(patient?.studies?.referrals?.ReferralsToOtherServices)) return null;

        return (
            patient?.studies.referrals.ReferralsToOtherServices.map((v) => {
                const flag = _.find(referralsDict, {snomedCode: v.ReferredToService_SnomedCt.snomedCode});
                const completed = !_.isEmpty(v?.ReferredToServiceAssessmentTime) && !_.isEmpty(v?.ReferredToServiceAssessmentDate);

                if (!flag) return null;
                return (
                    <Tooltip
                        title={flag?.__description}
                        key={flag?.snomedCode}
                    >
                        <div
                            className={clsx(classes.statusIconSpec, completed && 'completed')}
                            style={{

                                backgroundColor: getReferralsColor(flag),
                            }}
                        >
                            {flag?.notes}
                        </div>
                    </Tooltip>
                );
            })
        );
    };

    if (patientType === PATIENT_CONTEXT_TYPE.REVIEW) return null;

    return (
        <>
            {getReferralIcon()}
            {getSepsisIcon()}
            {getDementiaIcon()}
            {getInvestigationsIcon()}
            {getInfectionsIcon()}
            {getNextObsStatusIcon()}
            {getDLStatusIcon()}
            {pscIconStatus &&
                <PatientSafetyChecklistIconStatus
                    patient={patient}
                />
            }

            <DischargeLetterModal
                open={isDLSOpen}
                onClose={closeDLS}
                patient={patient}
            />
        </>
    );
};
export default MainScreenIcons;